import { loadPlan } from './loadPlan';
import type { Plan } from './loadPlan';
import { parseArc } from './arc';
import { buildBoard } from './board';
import { getVaultState, subscribeVault } from '../session/vaultStore';
import { queueSnapshot, subscribeQueue } from '../vault/writeQueue';
import { isArcIn } from '../vault/kinds';

/**
 * ONE parse of the plan, shared by every surface that reads arcs.
 *
 * Five screens called `loadPlan` for themselves — the board, the palette,
 * thoughts, a file's page and the plane — each re-reading and re-parsing every
 * arc in the vault. Beyond the waste, they answered differently: two loads
 * taken at different moments are two different plans, which is how the board
 * and the palette came to report different arc counts.
 *
 * Re-reading is centralised for the same reason, and it is a big read: every
 * arc file, decrypted. So it happens ONCE per edit, when the queue settles and
 * the vault holds what was written — or when the queue drops a batch, or a file
 * a newer app wrote, that the vault refused, and the vault holds what was there
 * before it. Until then the edit shows because the
 * queue is laid over the plan already held — the same overlay `loadPlan` does
 * at read time, applied the moment a write is queued.
 */
export type PlanState =
  | { status: 'idle' }
  | { status: 'loading'; done: number; total: number }
  | { status: 'ready'; plan: Plan }
  /** No message: what failed is the vault's business, and the vault state
   *  carries it. This says only that the plan is not readable. */
  | { status: 'error' };

let state: PlanState = { status: 'idle' };
const listeners = new Set<() => void>();
/** Same guard as the vault store: a load that answers after a newer one began
 *  is a stale plan, and publishing it would undo the newer read. */
let generation = 0;

function publish(next: PlanState): void {
  state = next;
  for (const listener of listeners) listener();
}

function load(): void {
  generation += 1;
  const mine = generation;

  const vault = getVaultState();
  if (vault.status !== 'ready') {
    publish({ status: 'idle' });
    return;
  }

  // A RE-read keeps the plan it is re-reading. Falling back to `loading` takes
  // the board off the screen for as long as the vault takes to decrypt, and
  // what comes back is the same board without the filter that was set, the
  // scroll position, or the row being worked on — all of them state in a
  // component that just unmounted.
  const held = state.status === 'ready' ? state.plan : null;
  if (!held) publish({ status: 'loading', done: 0, total: 0 });

  loadPlan(vault.vault, (done, total) => {
    if (mine === generation && !held) publish({ status: 'loading', done, total });
  })
    .then((plan) => {
      if (mine === generation) publish({ status: 'ready', plan });
    })
    .catch(() => {
      // A re-read that failed leaves the last good plan standing rather than
      // replacing a working board with an error screen. A first read has
      // nothing to stand on and says so.
      if (mine === generation && !held) publish({ status: 'error' });
    });
}

/**
 * What this tab is writing, over what it last read.
 *
 * Only paths the plan holds as arcs, or new ones in `arcs/`, and only where
 * the text actually differs, so a settled queue and a note being typed both
 * come through as nothing.
 */
function overlayPending(pending: ReadonlyMap<string, string>): void {
  if (state.status !== 'ready') return;
  const held = state.plan;
  const byPath = new Map(held.arcs.map((arc) => [arc.path, arc]));

  let changed = false;
  for (const [path, text] of pending) {
    if (!isArcIn(path, held.arcs) || byPath.get(path)?.raw === text) continue;
    byPath.set(path, parseArc(path, text));
    changed = true;
  }
  if (!changed) return;

  const arcs = [...byPath.values()];
  publish({
    status: 'ready',
    plan: { ...held, arcs, board: buildBoard(arcs, held.board.conflictCount) },
  });
}

subscribeVault(load);

let wasSettled = queueSnapshot().state === 'idle';
let lastDropped = queueSnapshot().dropped;
let lastRefused = queueSnapshot().refused;
subscribeQueue(() => {
  const { state: queue, pending, dropped, refused } = queueSnapshot();
  const settled = queue === 'idle';
  const justSettled = settled && !wasSettled;
  wasSettled = settled;
  const justDropped = dropped !== null && dropped !== lastDropped;
  lastDropped = dropped;
  const justRefused = refused.length > 0 && refused !== lastRefused;
  lastRefused = refused;
  // A settled queue means the vault now holds what the overlay was standing in
  // for, and that is the one moment worth re-reading for. A dropped batch is
  // the other: the overlay is showing edits the vault never took, and it only
  // ever adds. So is a file refused for being a newer app's, which leaves the
  // queue in `error` rather than settling. Every other notification is an edit
  // arriving, which the overlay answers without a read.
  if (justSettled || justDropped || justRefused) load();
  else overlayPending(pending);
});

load();

export function getPlanState(): PlanState {
  return state;
}

export function subscribePlan(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Re-read the vault for every surface. A write does not need this — the queue
 *  shows the edit at once and re-reads when it settles. */
export function reloadPlan(): void {
  load();
}
